<script setup lang="ts">
import { TimeMapEntry } from "@/models/TimeMapEntry";

const props = defineProps<{
  id?: string;
  timemap: TimeMapEntry[];
  midi: string;
  defaultBpm: number | null;
  sheetType: string;
  allowPreview: boolean;
}>();

const beta = useBetaStore();
const fs = useFullScreenStore();
const newMidiControls = computed(() => beta.features.midiScrollImprovements);
const playing = ref(false);
const windowWidth = ref(window.innerWidth);

const disableFullScreenAutoScroll = computed(() => {
  return isFirefox() && fs.fullScreenEnabled || isSafari(false) && fs.fullScreenEnabled
})

const autoScroll = useLocalStorage<boolean>(
  AppConstants.LOCALSTORAGE_KEYS.SHEETMUSIC_AUTOSCROLL,
  false,
) as globalThis.Ref<boolean>;

const fullMidiController = ref(false);
const bpm = ref();
const timer = ref();
const midiStore = useMidiStore();

watch(
  () => props.midi,
  () => {
    setDefaultBpm();
    midiStore.setAutoScroll(autoScroll.value);
  },
);

function setDefaultBpm() {
  bpm.value = props.defaultBpm || 120;
  if (midiStore.bpm) {
    bpm.value = midiStore.bpm;
  }
}

async function togglePlay() {
  if (!props.allowPreview || !props.midi) {
    return;
  }
  playing.value = !playing.value;
  await midiStore.togglePlay();
}

async function restartSong() {
  midiStore.restartSong();
}

async function toggleAutoScroll() {
  midiStore.toggleAutoScroll();
}

async function setFullMidiController(v: boolean) {
  midiStore.setFullMidiController(v);
}

function increaseBpm() {
  bpm.value = ++bpm.value;
  timer.value = setTimeout(increaseBpm, 100);
  updateBpm();
}

function clear() {
  clearTimeout(timer.value);
}

function decreaseBpm() {
  bpm.value = --bpm.value;
  timer.value = setTimeout(decreaseBpm, 100);
  updateBpm();
}

function updateBpm() {
  midiStore.setBpm(parseInt(bpm.value));
}

window.addEventListener(
  "resize",
  () => (windowWidth.value = window.innerWidth),
);

watch(
  () => props.timemap,
  () => {
    init();
  },
);

watch(
  () => fs.fullScreenEnabled,
  () => {
    if(disableFullScreenAutoScroll.value){
      midiStore.setAutoScroll(false);
    }
  }
)

watch(
  () => midiStore.playing,
  () => (playing.value = midiStore.playing),
);

watch(
  () => midiStore.autoScroll,
  () => (autoScroll.value = midiStore.autoScroll),
);

watch(
  () => midiStore.fullMidiController,
  () => (fullMidiController.value = midiStore.fullMidiController),
);

watch(
  () => windowWidth.value > 1279,
  () => midiStore.setFullMidiController(false),
);

watch(
  () => newMidiControls.value,
  () => midiStore.setFullMidiController(false),
);

onMounted(() => {
  init();
});

function init() {
  if (!props.timemap.length) {
    midiStore.clearSong();
    return;
  }
  setDefaultBpm();
  midiStore.preloadDataForPlayback(props.midi, props.timemap, props.defaultBpm);
}
</script>

<template>
  <div class="hidden xl:block">
    <label
      :for="id + 'TempoSelect'"
      class="label hidden xl:block"
      ><span class="label-text">{{ $t("SV.LIT_Playback") }}</span>
    </label>
    <div class="flex items-center gap-2">
      <button
        v-if="newMidiControls"
        :id="id + 'ToggleButton'"
        type="button"
        class="toggle-play"
        :data-playing="playing ? 'true' : 'false'"
        :aria-label="playing ? $t('SV.LIT_PauseMidi') : $t('SV.LIT_PlayMidi')"
        :data-content-type="sheetType"
        :title="$t('SV.LIT_RestartSong')"
        data-content-action="playback"
        @click="restartSong"
      >
        <FAIcon class="fa-solid fa-fw fa-backward-step" />
      </button>
      <button
        :id="id + 'ToggleButton'"
        type="button"
        class="toggle-play"
        :data-playing="playing ? 'true' : 'false'"
        :aria-label="playing ? $t('SV.LIT_PauseMidi') : $t('SV.LIT_PlayMidi')"
        :data-content-type="sheetType"
        data-content-action="playback"
        @click="togglePlay()"
      >
        <FAIcon
          class="fa-solid fa-fw toggle-play-icon"
          :class="playing ? 'fa-pause' : 'fa-play'"
        />
      </button>
      <button
        v-if="newMidiControls && !disableFullScreenAutoScroll"
        class="toggle-play"
        data-user-activity="Toggle Auto Scroll"
        :title="
          autoScroll
            ? $t('SV.LIT_DisableAutoScroll')
            : $t('SV.LIT_EnableAutoScroll')
        "
        @click="toggleAutoScroll"
      >
        <FAIcon
          class="fa-solid fa-right-from-line"
          :class="autoScroll ? 'auto-scroll-active' : 'auto-scroll'"
        />
      </button>
      <input
        :id="id + 'TempoSelect'"
        v-model="bpm"
        type="number"
        step="1"
        min="0"
        class="tempo-select input text-center"
        @change="updateBpm"
      />
    </div>
  </div>
  <!-- NEW MIDI CONTROLS TO TAKE FULL TOOL BAR IN MOBILE -->
  <button
    v-if="!fullMidiController && newMidiControls"
    class="midi-play-btn xl:hidden"
    :label="$t('SV.LIT_Playback')"
    icon="fa-play"
    html-only
    disable-content-auto-close
    @click="setFullMidiController(true)"
  >
    <FAIcon class="fa-light fa-play" />
  </button>
  <div
    v-else-if="newMidiControls"
    class="flex flex-wrap items-center xl:hidden"
  >
    <button
      :id="id + 'ToggleButton'"
      type="button"
      class="toggle-play me-3"
      title="Back"
      :data-content-type="sheetType"
      data-content-action="playback"
      @click="setFullMidiController(false)"
    >
      <FAIcon class="fa-light fa-fw fa-arrow-left" />
    </button>
    <button
      :id="id + 'ToggleButton'"
      type="button"
      class="toggle-play"
      :data-playing="playing ? 'true' : 'false'"
      :aria-label="playing ? $t('SV.LIT_PauseMidi') : $t('SV.LIT_PlayMidi')"
      :data-content-type="sheetType"
      :title="$t('SV.LIT_RestartSong')"
      data-content-action="playback"
      @click="restartSong"
    >
      <FAIcon class="fa-solid fa-fw fa-backward-step" />
    </button>
    <button
      :id="id + 'ToggleButton'"
      type="button"
      class="toggle-play"
      :data-playing="playing ? 'true' : 'false'"
      :title="playing ? $t('SV.LIT_PauseMidi') : $t('SV.LIT_PlayMidi')"
      :data-content-type="sheetType"
      data-content-action="playback"
      @click="togglePlay()"
    >
      <FAIcon
        class="fa-solid fa-fw toggle-play-icon"
        :class="playing ? 'fa-pause' : 'fa-play'"
      />
    </button>
    <button
      v-if="!disableFullScreenAutoScroll"
      class="toggle-play"
      :title="
        autoScroll
          ? $t('SV.LIT_DisableAutoScroll')
          : $t('SV.LIT_EnableAutoScroll')
      "
      @click="toggleAutoScroll"
    >
      <FAIcon
        class="fa-solid fa-right-from-line"
        :class="autoScroll ? 'auto-scroll-active' : 'auto-scroll'"
      />
    </button>
    <div class="flex items-center py-2">
      <button
        :id="id + 'decreaseBpmButton'"
        type="button"
        class="tempo-adjust-button"
        :title="$t('S.LIT_DecreaseTempo')"
        @mousedown="decreaseBpm()"
        @mouseup="clear"
        @keydown.enter="decreaseBpm()"
        @keyup.enter="clear"
      >
        <FAIcon class="fa-light fa-minus" />
      </button>
      <input
        :id="id + 'TempoSelect'"
        v-model="bpm"
        type="number"
        step="1"
        min="0"
        class="tempo-select input text-center"
        @change="updateBpm"
      />
      <button
        :id="id + 'increaseBpmButton'"
        type="button"
        class="tempo-adjust-button"
        :title="$t('S.LIT_IncreaseTempo')"
        @mousedown="increaseBpm()"
        @keydown.enter="increaseBpm()"
        @mouseup="clear"
        @keyup.enter="clear"
      >
        <FAIcon class="fa-light fa-plus" />
      </button>
    </div>
  </div>
  <!-- OLD MOBILE MIDI CONTROLS -->
  <ToolbarButtonDropdown
    v-if="!newMidiControls"
    class="xl:hidden"
    :label="$t('SV.LIT_Playback')"
    icon="fa-play"
    html-only
    disable-content-auto-close
  >
    <ToolbarButtonDropdownHeader>{{
      $t("SV.LIT_Playback")
    }}</ToolbarButtonDropdownHeader>
    <div class="flex p-2">
      <button
        v-if="newMidiControls"
        :id="id + 'ToggleButton'"
        type="button"
        class="toggle-play"
        :data-playing="playing ? 'true' : 'false'"
        :aria-label="playing ? $t('SV.LIT_PauseMidi') : $t('SV.LIT_PlayMidi')"
        :data-content-type="sheetType"
        :title="$t('SV.LIT_RestartSong')"
        data-content-action="playback"
        @click="restartSong"
      >
        <FAIcon class="fa-solid fa-fw fa-backward-step" />
      </button>
      <button
        :id="id + 'ToggleButton'"
        type="button"
        class="toggle-play"
        :data-playing="playing ? 'true' : 'false'"
        :title="playing ? $t('SV.LIT_PauseMidi') : $t('SV.LIT_PlayMidi')"
        :data-content-type="sheetType"
        data-content-action="playback"
        @click="togglePlay()"
      >
        <FAIcon
          class="fa-solid fa-fw toggle-play-icon"
          :class="playing ? 'fa-pause' : 'fa-play'"
        />
      </button>
      <button
        v-if="newMidiControls && !disableFullScreenAutoScroll"
        class="toggle-play"
        :title="
          autoScroll
            ? $t('SV.LIT_DisableAutoScroll')
            : $t('SV.LIT_EnableAutoScroll')
        "
        @click="toggleAutoScroll"
      >
        <FAIcon
          class="fa-solid fa-right-from-line"
          :class="autoScroll ? 'auto-scroll-active' : 'auto-scroll'"
        />
      </button>
      <div class="flex items-center py-2">
        <button
          :id="id + 'decreaseBpmButton'"
          type="button"
          class="tempo-adjust-button"
          :title="$t('S.LIT_DecreaseTempo')"
          @mousedown="decreaseBpm()"
          @mouseup="clear"
          @keydown.enter="decreaseBpm()"
          @keyup.enter="clear"
        >
          <FAIcon class="fa-light fa-minus" />
        </button>
        <input
          :id="id + 'TempoSelect'"
          v-model="bpm"
          type="number"
          step="1"
          min="0"
          class="tempo-select input"
          @change="updateBpm"
        />
        <button
          :id="id + 'increaseBpmButton'"
          type="button"
          class="tempo-adjust-button"
          :title="$t('S.LIT_IncreaseTempo')"
          @mousedown="increaseBpm()"
          @keydown.enter="increaseBpm()"
          @mouseup="clear"
          @keyup.enter="clear"
        >
          <FAIcon class="fa-light fa-plus" />
        </button>
      </div>
    </div>
  </ToolbarButtonDropdown>
</template>

<style scoped lang="postcss">
.toggle-play {
  @apply me-3 text-xl text-gray-500 transition-all xl:me-1;
  &:hover,
  &:focus-visible {
    @apply text-gray-800;
  }
}
.toggle-play-icon {
  @apply text-xl;
}
.tempo-select {
  width: 75px;
}
.tempo-adjust-button {
  @apply aspect-square rounded-full px-2 transition;
  &:hover,
  &:focus-visible {
    @apply bg-light-700 text-black;
    i,
    svg {
      @apply text-black;
    }
  }
}

.auto-scroll {
  @apply text-xl;
}

.auto-scroll-active {
  @apply text-xl text-ccli-blue;
}

.midi-play-btn {
  @apply min-h-fit flex-nowrap gap-1 whitespace-nowrap rounded border-none bg-transparent px-2 py-3 text-gray-500 shadow-none;
  font-size: 1.25em;
  font-weight: 400;
  text-transform: unset;
}
</style>
